import {Router} from '@angular/router';
import * as moment from 'moment';

import {DefaultItemsPerPage, ItemsPerPageSelector} from '../../../shared/widgets';
import {CacheCollectionService} from '../../../shared/services';
import {InitialComponentDataValidationException} from '../../../shared/exceptions';

import {EventsService} from '../services';
import {LayoutConfigInterface} from '../components';

import {ErrorMessageIdSelector} from '../../../../assets/descriptors';
import {Subscription} from 'rxjs';
import {ComponentStorageKeyProvider} from 'appShared/providers';

export abstract class BaseComponent {
    protected router: Router;
    protected cacheCollectionService: CacheCollectionService;

    public layoutConfig: LayoutConfigInterface;
    public hiddenFilter: boolean;
    public eventsService: EventsService;
    public showLoader = false;
    public id: string;
    public componentStorageKeyProvider: ComponentStorageKeyProvider;
    public cachedPaginationOption: number;

    protected constructor(
        eventsService: EventsService,
        router?: Router,
        cacheCollectionService?: CacheCollectionService
    ) {
        this.eventsService = eventsService;
        this.router = router;
        this.cacheCollectionService = cacheCollectionService;
        this.id = this.constructor.name;
    }

    /**
     * Components rendered for several entities - the tabs of a client for instance -
     * override this so that their cached state is kept per entity instead of being
     * shared by every entity the component is rendered for.
     */
    protected getStorageScope(): string | number {
        return null;
    }

    protected unsubscribeBySubscription(subscription: Subscription): void {
        if (subscription) {
            subscription.unsubscribe();
        }
    }

    abstract getTitle(): string;

    abstract getText(): string;

    abstract getLayoutConfig(): LayoutConfigInterface;

    abstract validateInitialData(data: any): void;

    onInit(): void {
        this.componentStorageKeyProvider = new ComponentStorageKeyProvider(this.id, this.getStorageScope());
        this.layoutConfig = this.getLayoutConfig();
        const hiddenFilterCache = this.cacheCollectionService?.getItem(this.componentStorageKeyProvider.getHiddenFilterKey())?.data;
        this.hiddenFilter = hiddenFilterCache?.hiddenFilter ?? true;
        const itemPerPageCache = this.cacheCollectionService?.getItem(this.componentStorageKeyProvider.getFilterValueKey())?.data;
        this.cachedPaginationOption = itemPerPageCache ? itemPerPageCache[ItemsPerPageSelector] : undefined;
    }

    afterViewInit(): void {
        setTimeout(() => {
            this.eventsService.viewTitle.next(this.getTitle());
            this.eventsService.viewText.next(this.getText());
        });
    }

    onDestroy(): void {
    }

    /**
     * Methods used in the components for reports
     */

    paginateCollection<DataType>(collection: DataType[], start = 0, end = DefaultItemsPerPage): DataType[] {
        return collection.filter((item: DataType, index: number) => {
            return index >= start && index < end;
        });
    }

    toggleFilter(): void {
        this.hiddenFilter = !this.hiddenFilter;
        this.cacheCollectionService?.addItem(this.componentStorageKeyProvider.getHiddenFilterKey(), {hiddenFilter: this.hiddenFilter});
    }

    hasLastFilterCache(): boolean {
        return this.cacheCollectionService.hasItem(this.componentStorageKeyProvider.getFilterValueKey());
    }

    protected cacheFilterValues(data: { [key: string]: string | number | boolean | string[] | number[] | boolean[] }): void {
        if (!this.cacheCollectionService) {
            return;
        }

        const keyProvider = this.componentStorageKeyProvider
            ?? new ComponentStorageKeyProvider(this.id, this.getStorageScope());

        this.cacheCollectionService.addItem(keyProvider.getFilterValueKey(), data);
    }

    /** ******************************************* */

    doInitialDataValidationCheck<T>(data: T, cb: () => void) {
        try {
            this.validateInitialData(data);

            cb();
        } catch (exception) {
            const timestamp = moment().unix();

            if (exception instanceof InitialComponentDataValidationException) {
                const messages = exception.messages;

                if (this.cacheCollectionService) {
                    this.cacheCollectionService.addItem(timestamp.toString(), messages);
                }

                this.router.navigate(['/error'], {
                    queryParams: {
                        [ErrorMessageIdSelector]: timestamp
                    }
                }).finally();
            } else {
                console.error(exception);
            }
        }
    }
}
